<template>
  <div class="event-header__top-extra" v-if="event.status === 'confirmed'">
    <div class="event-header__top-extra-title">
      <template v-if="!isEventMontage">
        <span class="event-header__stand-number">
          {{ $t("event.header.standNumber") }}:
        </span>

        <div v-if="event.stands?.length" class="event-header__stand-list">
          <template v-if="event.stands.length < 3">
            <div
              class="event-header__stand-box"
              v-for="item in event.stands"
              :key="item.id"
            >
              <span class="event-header__stand-item">
                {{ item.title }}
              </span>

              <Tooltip position="bottom">
                <icon
                  name="help-filled"
                  class="event-header__top-extra-title-info"
                  width="16px"
                  height="16px"
                />
                <template #content>
                  <table class="event-header__top-extra-title-table">
                    <tbody>
                      <tr>
                        <td style="padding-right: 10px;">
                          {{ $t("event.header.pavilion") }}
                        </td>
                        <td>{{ $t("event.header.hall") }}</td>
                      </tr>
                      <tr>
                        <td>{{ getPavilionName(item.pavilion) || "—" }}</td>
                        <td>{{ getHallName(item.pavilion) || "—" }}</td>
                      </tr>
                    </tbody>
                  </table>
                </template>
              </Tooltip>
            </div>
          </template>
          <template v-else>
            <span class="event-header__stand-amount">
              {{ event.stands.length }}
            </span>
            <tooltip
              @open="open"
              @close="close"
              clickable
              position="bottom"
              custom-class="event-header__stand-tooltip-content"
            >
              <icon
                name="arrow-down_grey"
                :class="{ _rotated: isStandListOpened }"
              />
              <template #content>
                <div class="event-header__stand-inner">
                  <div
                    class="event-header__stand-box"
                    v-for="item in event.stands"
                    :key="item.id"
                  >
                    <span class="event-header__stand-item">
                      {{ item.title }}</span
                    >

                    <Tooltip position="bottom">
                      <icon
                        name="help-filled"
                        class="event-header__top-extra-title-info"
                        width="16px"
                        height="16px"
                      />
                      <template #content>
                        <table class="event-header__top-extra-title-table">
                          <tbody>
                            <tr>
                              <td style="padding-right: 10px;">
                                {{ $t("event.header.pavilion") }}
                              </td>
                              <td>{{ $t("event.header.hall") }}</td>
                            </tr>
                            <tr>
                              <td>
                                {{ getPavilionName(item.pavilion) || "—" }}
                              </td>
                              <td>
                                {{ getHallName(item.pavilion) || "—" }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </template>
                    </Tooltip>
                  </div>
                </div>
              </template>
            </tooltip>
          </template>
        </div>

        <div v-else class="event-header__stand-list">
          <div class="event-header__stand-item">
            {{ event.stand || "—" }}

            <Tooltip position="bottom">
              <icon
                name="help-filled"
                class="event-header__top-extra-title-info"
                width="16px"
                height="16px"
              />
              <template #content>
                <table>
                  <tbody>
                    <tr>
                      <td style="padding-right: 10px;">
                        {{ $t("event.header.pavilion") }}
                      </td>
                      <td>{{ $t("event.header.hall") }}</td>
                    </tr>
                    <tr>
                      <td>{{ pavilionName || "—" }}</td>
                      <td>{{ hallName || "—" }}</td>
                    </tr>
                  </tbody>
                </table>
              </template>
            </Tooltip>
          </div>
        </div>
      </template>

      <template v-else>
        {{ companyName }}
      </template>
    </div>

    <p class="event-header__top-extra-text">{{ participantType }}</p>

    <p
      class="event-header__top-extra-text"
      v-if="isEventSubexponent && isEventSubexponentViewedByOrganizer"
    >
      {{ $t("event.header.colorg") }}: {{ parentCompanyName }}
    </p>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { i18n } from "@v2/shared/config";
import { Tooltip } from "@v2/shared/ui/components";

export default {
  name: "EventStand",
  components: { Tooltip },
  data() {
    return {
      isStandListOpened: false
    };
  },
  computed: {
    ...mapState("event", ["event", "companies"]),
    ...mapGetters("event", [
      "isEventSubexponent",
      "isEventSubexponentViewedByOrganizer",
      "isEventMontage",
      "participantType",
      "companyName"
    ]),
    parentCompanyName() {
      if (!(this.event && this.event.parent && this.event.parent.company)) {
        return "";
      }

      return i18n.locale === "en"
        ? this.event.parent.company.name_eng
        : this.event.parent.company.name_rus;
    },

    pavilionName() {
      const pavilion = this.event ? this.event.pavilion_rus || "" : "";
      return pavilion.replace(/павильон/gi, "").trim();
    },

    hallName() {
      const pavilion = this.event ? this.event.hall_rus || "" : "";
      return pavilion.replace(/зал/gi, "").trim();
    }
  },

  methods: {
    /**
     * Returns the pavilion name for a given hall, localized and sanitized.
     * Gracefully handles missing parent or titles by returning a fallback ("-").
     *
     * @param {Object} hall - The hall object containing parent info
     * @returns {string} Localized pavilion name or "-" if unavailable
     */
    getPavilionName(hall) {
      if (!hall?.parent) return "-";

      const { locale } = i18n;
      const { title_eng, title_rus } = hall.parent;

      const localizedTitle = locale === "en" ? title_eng : title_rus;
      if (!localizedTitle) return "-";

      const sanitizedTitle = localizedTitle
        .replace(
          new RegExp(locale === "en" ? "pavilion" : "павильон", "gi"),
          ""
        )
        .trim();

      return sanitizedTitle || "-";
    },

    getHallName(hall) {
      const title =
        i18n.locale === "en"
          ? hall.title_eng.replace(/зал/gi, "")
          : hall.title_rus.replace(/зал/gi, "");
      return title || "-";
    },
    close() {
      this.isStandListOpened = false;
    },
    open() {
      this.isStandListOpened = true;
    }
  }
};
</script>

<style lang="scss">
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";
.event-header__top {
  &-extra {
    width: 300px;
    background: #f0f5f9;

    flex-shrink: 0;

    padding: 15px 17px 15px 26px;

    box-sizing: border-box;

    letter-spacing: 0.15px;

    align-self: flex-start;

    @include respond-down(lg) {
      flex: 1;
      padding: 15px 5px 18px 23px;
    }
    @include respond-down(md) {
      flex: none;
      max-width: 100%;
      padding: 17px 25px 18px 27px;
    }
    &-title {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: flex-start;
      align-content: flex-start;
      align-items: flex-start;
      width: 100%;

      font-size: 16px;
      font-weight: normal;
      line-height: 1.5;

      color: #000000;

      margin: 0 0 10px;

      position: relative;

      @include respond-down(lg) {
        margin-bottom: 5px;
      }
      @include respond-down(md) {
        margin-bottom: 8px;
      }
      &-table {
        width: 100%;

        tr:first-child {
          font-size: 12px;
          line-height: 1.33;

          color: #6d7179;
        }

        tr:last-child {
          font-size: 16px;
          font-weight: 500;
          color: #505050;
          line-height: 1.5;

          td {
            padding-top: 5px;
          }
        }
      }
    }

    &-text {
      margin: 3px 0 0 0;

      font-size: 13px;
      line-height: 1.54;

      @include respond-down(lg) {
        font-size: 12px;
      }
    }
    .svg-icon {
      width: 12px;
      height: 12px;
      &._rotated {
        transform: rotate(180deg);
      }
    }
  }
}
.event-header {
  &__stand-number {
    margin-right: 3px;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    color: #989898;

    @include respond-down(lg) {
      margin-right: 5px;
      font-size: 14px;
    }
    @include respond-down(md) {
      margin-right: 3px;
    }
  }
  &__stand {
    &-box {
      display: flex;
      margin-right: 8px;
    }
    &-tooltip-content {
      max-height: 500px;
      overflow-y: scroll;
    }
    &-inner {
      display: grid;
      grid-template-columns: repeat(2, minmax(60px, 1fr));
      gap: 10px;
    }
    &-content {
      background-color: #fff;
      box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.1);
      border-radius: 2px;
    }
    &-list {
      flex: 1;
      margin-right: -10px;
      display: flex;
      flex-wrap: wrap;
    }
    &-item {
      display: flex;
      vertical-align: top;
      position: relative;
      margin-right: 3px;
      font-weight: 500;
      font-size: 15px;
      line-height: 24px;
      color: #505050;
      width: fit-content;
      @include respond-down(lg) {
        font-size: 14px;
      }
    }
    &-amount {
      font-weight: 500;
      font-size: 16px;
      line-height: 24px;
      margin-right: 5px;
    }
  }
}
</style>
